import { useEffect, useState, useCallback, useRef } from "react";
import UnifiedSessionService from "../../services/UnifiedSessionService";
import PatientFormService from "../../services/PatientFormService";
import Buttons from "../../components/UI/Buttons";
import ActivityCharts from "../EmployeeVisitContainer/EmployeeVisitDetailView/RightContent/ActivityChart";
import VisitsService from "../../services/VisitsService";

const UnifiedStepRouter = ({
  token,
  sessionData,
  setSessionData,
  onComplete,
  Loader,
  Toast,
}) => {
  const { stepManifest, currentStep, totalSteps, patientFormId, visitId, employeeId, organization_id: sessionOrganizationId } = sessionData;
  const currentStepData = stepManifest[currentStep];
  const [patientFormCompleted, setPatientFormCompleted] = useState(false);

  const advanceStep = useCallback(async () => {
    Loader(true);
    const result = await UnifiedSessionService.completeStep(token, currentStep, patientFormId);
    Loader(false);
    if (!result || !result.data) return Toast.error("Failed to advance to next step.");
    if (result.data.is_last_step) return onComplete();
    setSessionData((prev) => ({ ...prev, currentStep: result.data.next_step }));
  }, [token, currentStep, patientFormId, Loader, Toast, onComplete, setSessionData]);

  const handlePatientFormComplete = useCallback(async () => {
    const patientFormStepCount = stepManifest.filter((s) => s.type === "patient_form").length;
    for (let i = currentStep; i < currentStep + patientFormStepCount && i < totalSteps; i++) {
      if (stepManifest[i]?.type !== "patient_form") break;
      const result = await UnifiedSessionService.completeStep(token, i, patientFormId);
      if (result?.data?.is_last_step) return onComplete();
    }
    const next = stepManifest.findIndex((s, idx) => idx >= currentStep && s.type !== "patient_form");
    if (next >= 0) setSessionData((prev) => ({ ...prev, currentStep: next }));
    else onComplete();
    setPatientFormCompleted(true);
  }, [stepManifest, currentStep, totalSteps, token, patientFormId, onComplete, setSessionData]);

  if (!currentStepData) return null;
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="unified-step-router">
      <div className="unified-progress-bar px-3 py-2" style={{ background: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <small className="text-muted font-weight-bold">Step {currentStep + 1} of {totalSteps}</small>
          <small className="text-muted">{getStepLabel(currentStepData)}</small>
        </div>
        <div className="progress" style={{ height: "6px" }}>
          <div className="progress-bar bg-success" role="progressbar" style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      <div className="unified-step-content">
        {currentStepData.type === "patient_form" && !patientFormCompleted ? (
          <PatientFormUnified patientFormId={patientFormId} onComplete={handlePatientFormComplete} Loader={Loader} Toast={Toast} />
        ) : currentStepData.type === "questionnaire" ? (
          <QuestionnaireStep
            activityMappingId={currentStepData.activity_mapping_id}
            visitId={visitId}
            employeeId={employeeId}
            organizationId={sessionOrganizationId}
            onComplete={advanceStep}
            Loader={Loader}
            Toast={Toast}
          />
        ) : null}
      </div>
    </div>
  );
};

const PatientFormUnified = ({ patientFormId, onComplete, Loader, Toast }) => (
  <div className="unified-patient-form-wrapper">
    <PatientFormEmbedded patientFormId={patientFormId} onFormComplete={onComplete} Loader={Loader} Toast={Toast} />
  </div>
);

const PatientFormEmbedded = ({ patientFormId, onFormComplete, Loader, Toast }) => {
  const [formData, setFormData] = useState({});
  const [patientFormData, setPatientFormData] = useState({});
  const [validation, setValidation] = useState({});
  const [currentKey, setCurrentKey] = useState(null);

  const fetchPatientForms = async () => {
    Loader(true);
    const response = await PatientFormService.getPatientFormsById(patientFormId);
    Loader(false);
    setPatientFormData(response);
    const formPriority = [
      "patient_demographics_verification",
      "new_patient_intake_form",
      "actvity_consents",
      "emr_history",
      "patient_medical_history",
      "medical_history_questionnaire",
      "upload_required_id",
      "upload_additional_documents",
    ];
    if (response.patient_demographics_verification === 1 && !response.demographics_verified_at) {
      setCurrentKey("patient_demographics_verification");
    } else {
      const formKey = formPriority.find((key) => (key === "new_patient_intake_form" ? response[key] && response.consent_form : response[key] === 1));
      if (formKey) setCurrentKey(formKey);
      else onFormComplete();
    }
    setFormData({});
  };

  useEffect(() => {
    fetchPatientForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAndAdvance = async () => {
    Loader(true);
    const response = await PatientFormService.getPatientFormsById(patientFormId);
    Loader(false);
    setPatientFormData(response);
    const formPriority = [
      "patient_demographics_verification",
      "new_patient_intake_form",
      "actvity_consents",
      "emr_history",
      "patient_medical_history",
      "medical_history_questionnaire",
      "upload_required_id",
      "upload_additional_documents",
    ];
    const remaining = formPriority.filter((key) => {
      if (key === currentKey) return false;
      if (key === "new_patient_intake_form") return response[key] && response.consent_form;
      return response[key] === 1;
    });
    const currentIdx = formPriority.indexOf(currentKey);
    const nextKey = remaining.find((k) => formPriority.indexOf(k) > currentIdx);
    if (nextKey) {
      setCurrentKey(nextKey);
      setFormData({});
      setValidation({});
    } else {
      onFormComplete();
    }
  };

  const saveActivityConsents = async () => {
    const activities = Object.values(patientFormData.activity_consents || []);
    let hasError = false;
    let updateValidation = {};
    for (let index = 0; index < activities.length; index++) {
      const activity = activities[index];
      const payload = {
        visit_id: patientFormData.visit_id,
        activity_id: activity.activity_id,
        authorization_message_checked: !!formData[`authorization_message_checked_${index}`],
        declination_message_checked: !!formData[`declination_message_checked_${index}`],
        reasons_for_declination: formData[`reasons_for_declination_${index}`] || "",
        is_other_reason: !!formData[`is_other_reason_${index}`],
        other_reason: formData[`other_reason_${index}`] || "",
        patient_signature: formData.patient_signature,
        consent_date: formData.consent_date,
        patient_id: patientFormData.employee_id,
        company_id: patientFormData.company_id,
        organization_uuid: patientFormData.organization?.organization_uuid || patientFormData.organization_id,
        visit_mapping_id: activity.mapping_id,
      };
      const response = await PatientFormService.saveActivityConsentForm(activity.mapping_id, payload);
      if (response?.status === "ERROR") {
        hasError = true;
        if (typeof response.data === "object") {
          for (let key in response.data) updateValidation[key] = response.data[key].join(",");
        } else {
          Toast.error(response.data || "Failed to save activity consent.");
        }
      }
    }
    if (hasError) {
      setValidation(updateValidation);
      return false;
    }
    return true;
  };

  const saveDemographicsVerification = async () => {
    const payload = {
      ...formData,
      patient_form_id: patientFormId,
      visit_id: patientFormData.visit_id,
      employee_id: patientFormData.employee_id,
      organization_id: patientFormData.organization_id,
      key: "patient_demographics_verification",
    };
    const response = await PatientFormService.saveDemographicsVerification(payload, {});
    if (response?.status === "ERROR") {
      let updateValidation = {};
      if (typeof response.data === "object") {
        for (let key in response.data) updateValidation[key] = response.data[key].join(",");
      } else {
        Toast.error(response.data || "Failed to save demographics verification.");
      }
      if (Object.keys(updateValidation).length > 0) setValidation(updateValidation);
      return false;
    }
    return true;
  };

  const onSubmitHandler = async () => {
    try {
      Loader(true);
      if (currentKey === "patient_demographics_verification") {
        const ok = await saveDemographicsVerification();
        Loader(false);
        if (!ok) return;
        return refreshAndAdvance();
      }
      if (currentKey === "actvity_consents") {
        const ok = await saveActivityConsents();
        Loader(false);
        if (!ok) return;
        return refreshAndAdvance();
      }
      let payload;
      const headers = {};
      if (["upload_required_id", "upload_additional_documents"].includes(currentKey)) {
        payload = new FormData();
        payload.append("file", formData.file || "");
        payload.append("employee_id", patientFormData.employee_id);
        payload.append("document_type", currentKey);
        payload.append("key", currentKey);
        payload.append("patient_form_id", patientFormId);
        headers["Content-Type"] = "multipart/form-data";
      } else {
        payload = {
          ...formData,
          key: currentKey,
          visit_id: patientFormData.visit_id,
          employee_id: patientFormData.employee_id,
          organization_id: patientFormData.organization_id,
          patient_form_id: patientFormData.patient_form_id,
        };
        if (currentKey === "new_patient_intake_form" && patientFormData.consent_form) {
          payload.title_en = patientFormData.consent_form.title_en;
          payload.consent_form_content_en = patientFormData.consent_form.consent_form_content_en;
          payload.acknowledgement_en = patientFormData.consent_form.acknowledgement_en;
        }
      }
      const response = await PatientFormService.savePatientForm(payload, headers);
      Loader(false);
      if (response && response.status === "ERROR") {
        let updateValidation = {};
        if (typeof response.data === "object") {
          for (let key in response.data) updateValidation[key] = response.data[key].join(",");
        } else {
          Toast.error(response.data);
        }
        if (Object.keys(updateValidation).length > 0) return setValidation(updateValidation);
      }
      refreshAndAdvance();
    } catch (e) {
      Loader(false);
      Toast.error("Failed to submit form.");
    }
  };

  if (!currentKey) return <div className="text-center p-4">Loading forms...</div>;

  const FormComponents = {
    patient_demographics_verification: require("../PatientFormContainer/Forms/DemographicsVerification").default,
    new_patient_intake_form: require("../PatientFormContainer/Forms/ConsentForm").default,
    actvity_consents: require("../PatientFormContainer/Forms/ActivityConsent").default,
    emr_history: require("../PatientFormContainer/Forms/EmrHistoryQuestions").default,
    patient_medical_history: require("../PatientFormContainer/Forms/PatientQuestion").default,
    upload_required_id: require("../PatientFormContainer/Forms/UploadId").default,
    upload_additional_documents: require("../PatientFormContainer/Forms/UploadAdditionalDocument").default,
  };
  const StepComponent = FormComponents[currentKey];
  if (!StepComponent) return null;

  return (
    <div className="sh_cardBox sh_cardbglight">
      <div className="sh_cardHeader"><h6 className="text-lg f-500 mb-0 ml-1">Patient Form</h6></div>
      <div className="sh_cardBody p-0">
        <StepComponent
          formData={formData}
          patientFormData={patientFormData}
          onChangeHandler={(name, value) => {
            setFormData({ ...formData, [name]: value });
            setValidation({ ...validation, [name]: "" });
          }}
          bulkOnChangeHandler={setFormData}
          validation={validation}
          setValidation={setValidation}
          onSubmitHandler={onSubmitHandler}
          Loader={Loader}
          Toast={Toast}
          currentKey={currentKey}
          patient_form_id={patientFormId}
        />
      </div>
      <div className="sh_cardFooter d-flex justify-content-center">
        <Buttons clickHandler={onSubmitHandler} className="btn btn-primary" acl={"any"} label={"Submit"} />
      </div>
    </div>
  );
};

const QuestionnaireStep = ({
  activityMappingId,
  visitId,
  employeeId,
  organizationId,
  onComplete,
  Loader,
  Toast,
  patientFacing = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [chartDetails, setChartDetails] = useState(null);
  const [visitDetails, setVisitDetails] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const submittedSuccessfullyRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        Loader(true);
        let visitResponse;
        if (organizationId) {
          visitResponse = await VisitsService.getVisitsWithOrgId(organizationId, visitId);
        } else {
          visitResponse = await VisitsService.getVisits(visitId);
        }
        if (visitResponse === false || visitResponse == null) {
          Toast.error(
            organizationId
              ? "Unable to load questionnaire details."
              : "Unable to load questionnaire (missing clinic context). Open this step from your patient link again or contact support."
          );
          return;
        }
        const visit = Array.isArray(visitResponse) ? visitResponse[0] : visitResponse?.[0] || visitResponse;
        if (!visit) {
          Toast.error("Unable to load questionnaire details.");
          return;
        }
        const mappingActivity = (visit.mapping_activity || []).find((item) => item.mapping_id === activityMappingId);
        if (!mappingActivity || !mappingActivity.activity) {
          Toast.error("Questionnaire activity not found for this visit.");
          return;
        }
        setVisitDetails(visit);
        setEmployeeDetails(visit.employee || { employee_id: employeeId });
        setCompanyDetails(visit.company || {});
        setChartDetails({
          ...mappingActivity.activity,
          mapping_id: activityMappingId,
          visit_id: visitId,
          questionnaires_status: mappingActivity.questionnaires_status,
        });
      } catch (e) {
        Toast.error("Failed to load questionnaire.");
      } finally {
        setLoading(false);
        Loader(false);
      }
    };
    load();
  }, [activityMappingId, visitId, employeeId, organizationId, Loader, Toast]);

  const handleChartClose = () => {
    if (submittedSuccessfullyRef.current) {
      submittedSuccessfullyRef.current = false;
      onComplete();
    }
  };

  if (loading) return <div className="text-center p-4">Loading questionnaire...</div>;
  if (!chartDetails || !visitDetails || !employeeDetails) return <div className="text-center p-4 text-danger">Questionnaire could not be loaded.</div>;

  return (
    <div className="questionnaire-step p-2">
      <ActivityCharts
        chartDetails={chartDetails}
        suppressPatientPostSaveLogout={patientFacing}
        isOpen={true}
        visitDetails={visitDetails}
        fetchVisitDetails={() => {
          submittedSuccessfullyRef.current = true;
        }}
        stateList={[]}
        employeeDetails={employeeDetails}
        lookupsOptions={{}}
        companyDetails={companyDetails}
        toogleHanlder={handleChartClose}
        employeeId={employeeDetails.employee_id || employeeId}
        Toast={Toast}
        Loader={Loader}
      />
    </div>
  );
};

const getStepLabel = (step) => {
  if (!step) return "";
  if (step.type === "patient_form") {
    const labels = {
      patient_demographics_verification: "Demographics Verification",
      new_patient_intake_form: "Patient Intake Form",
      actvity_consents: "Activity Consents",
      emr_history: "EMR History",
      patient_medical_history: "Medical History",
      medical_history_questionnaire: "Medical History Questionnaire",
      upload_required_id: "Upload Required ID",
      upload_additional_documents: "Upload Additional Documents",
    };
    return labels[step.step] || step.step;
  }
  return "Health Questionnaire";
};

export default UnifiedStepRouter;
export { QuestionnaireStep };

